(function (root, factory) {
  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.ZugBannerPricing = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var EPSILON = 1e-9;
  var MATERIAL_TAG_PREFIX = 'zug_mat:';

  function toFiniteNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function normalizeDimension(feet, inches) {
    var feetValue = toFiniteNumber(feet, 0);
    var inchesValue = toFiniteNumber(inches, 0);

    if (feetValue < 0 || inchesValue < 0 || !Number.isInteger(feetValue)) {
      return NaN;
    }

    return feetValue * 12 + inchesValue;
  }

  function validateConfig(config) {
    if (!config || !Array.isArray(config.tiers) || config.tiers.length === 0) {
      throw new Error('Banner pricing requires at least one pricing tier.');
    }

    var previousSquareFeet = -Infinity;
    var previousRate = Infinity;

    config.tiers.forEach(function (tier) {
      var squareFeet = toFiniteNumber(tier.square_feet, NaN);
      var rate = toFiniteNumber(tier.price_per_square_foot, NaN);

      if (!Number.isFinite(squareFeet) || squareFeet <= 0 || squareFeet <= previousSquareFeet) {
        throw new Error('Banner pricing tier square-foot anchors must strictly increase.');
      }

      if (!Number.isFinite(rate) || rate < 0 || rate > previousRate + EPSILON) {
        throw new Error('Banner pricing rates must not increase as square footage increases.');
      }

      previousSquareFeet = squareFeet;
      previousRate = rate;
    });

    var minimum = toFiniteNumber(config.minimum_price_per_banner, NaN);
    if (!Number.isFinite(minimum) || minimum < 0) {
      throw new Error('Banner minimum price must be zero or greater.');
    }

    var pricingKey = String(config.pricing_key || '').trim();
    var materialRecipes = config.material_recipes || {};
    if (!pricingKey || Object.keys(materialRecipes).length === 0) {
      throw new Error('Banner pricing requires material recipes and a base pricing key.');
    }

    Object.keys(materialRecipes).forEach(function (materialKey) {
      var recipe = materialRecipes[materialKey] || {};
      var multiplier = toFiniteNumber(recipe.square_foot_rate_multiplier, NaN);
      var materialMinimum = toFiniteNumber(recipe.minimum_price_per_banner, NaN);

      if (String(recipe.pricing_key || '').trim() !== pricingKey) {
        throw new Error('Banner material recipes must reference the loaded base pricing key.');
      }
      if (!Number.isFinite(multiplier) || multiplier <= 0) {
        throw new Error('Banner material rate multipliers must be greater than zero.');
      }
      if (!Number.isFinite(materialMinimum) || materialMinimum < 0) {
        throw new Error('Banner material minimum prices must be zero or greater.');
      }
    });

    return true;
  }

  function resolveMaterialPricing(config, materialRecipe) {
    validateConfig(config);

    var recipe = materialRecipe ||
      (config.material_recipes && config.material_recipes[config.pricing_key]);
    if (!recipe || String(recipe.pricing_key || '').trim() !== String(config.pricing_key || '').trim()) {
      throw new Error('Banner material pricing does not reference the loaded base pricing table.');
    }

    return {
      multiplier: toFiniteNumber(recipe.square_foot_rate_multiplier, NaN),
      minimumPricePerBanner: toFiniteNumber(recipe.minimum_price_per_banner, NaN)
    };
  }

  function interpolateRate(config, totalSquareFeet) {
    validateConfig(config);

    var current = toFiniteNumber(totalSquareFeet, NaN);
    if (!Number.isFinite(current) || current < 0) {
      throw new Error('Total square footage must be zero or greater.');
    }

    var tiers = config.tiers;
    if (current <= tiers[0].square_feet) {
      return Number(tiers[0].price_per_square_foot);
    }

    for (var index = 1; index < tiers.length; index += 1) {
      var upper = tiers[index];
      var lower = tiers[index - 1];

      if (Math.abs(current - upper.square_feet) < EPSILON) {
        return Number(upper.price_per_square_foot);
      }

      if (current < upper.square_feet) {
        var ratio = (current - lower.square_feet) / (upper.square_feet - lower.square_feet);
        return Number(lower.price_per_square_foot) +
          ratio * (Number(upper.price_per_square_foot) - Number(lower.price_per_square_foot));
      }
    }

    return Number(tiers[tiers.length - 1].price_per_square_foot);
  }

  function dimensionWithinLimits(value, minimum, maximum) {
    var dimension = toFiniteNumber(value, NaN);
    var lower = toFiniteNumber(minimum, NaN);
    var upper = toFiniteNumber(maximum, NaN);

    return Number.isFinite(dimension) &&
      Number.isFinite(lower) &&
      Number.isFinite(upper) &&
      lower <= upper &&
      dimension >= lower &&
      dimension <= upper;
  }

  function resolveMaterialTag(tags, recipes) {
    var supportedRecipes = recipes || {};
    var resolvedKey = '';

    (Array.isArray(tags) ? tags : []).some(function (tag) {
      var normalizedTag = String(tag || '').trim().toLowerCase();
      if (normalizedTag.indexOf(MATERIAL_TAG_PREFIX) !== 0) return false;

      var candidate = normalizedTag.slice(MATERIAL_TAG_PREFIX.length).trim();
      if (!Object.prototype.hasOwnProperty.call(supportedRecipes, candidate)) return false;
      resolvedKey = candidate;
      return true;
    });

    if (!resolvedKey) {
      return {
        valid: false,
        key: '',
        recipe: null,
        error: 'A supported ZUG_MAT product tag is required.'
      };
    }

    return {
      valid: true,
      key: resolvedKey,
      recipe: supportedRecipes[resolvedKey],
      error: ''
    };
  }

  function buildAdjustmentBundle(input) {
    var totalCents = Math.round(toFiniteNumber(input.totalCents, NaN));
    var basePriceCents = Math.round(toFiniteNumber(input.basePriceCents, NaN));
    var baseQuantity = Math.round(toFiniteNumber(input.baseQuantity, 1));
    var baseVariantId = Number(input.baseVariantId);
    var dollarVariantId = Number(input.dollarVariantId);
    var centVariantId = Number(input.centVariantId);
    var uid = String(input.uid || '').trim();

    if (!Number.isInteger(totalCents) || totalCents < 1 ||
        !Number.isInteger(basePriceCents) || basePriceCents < 0 ||
        !Number.isInteger(baseQuantity) || baseQuantity < 1 ||
        !Number.isInteger(baseVariantId) || baseVariantId < 1 ||
        !Number.isInteger(dollarVariantId) || dollarVariantId < 1 ||
        !Number.isInteger(centVariantId) || centVariantId < 1 ||
        uid === '') {
      throw new Error('Banner cart bundle configuration is incomplete.');
    }

    var adjustmentCents = totalCents - (basePriceCents * baseQuantity);
    if (adjustmentCents < 0) {
      throw new Error('Calculated banner total is below the Shopify base line price.');
    }

    var dollarQuantity = Math.floor(adjustmentCents / 100);
    var centQuantity = adjustmentCents % 100;
    var adjustmentProperties = {
      '_pricing_engine': 'true',
      '_line_type': 'adjustment',
      'calc_line_uid': uid,
      '_banner_bundle_role': 'adjustment'
    };
    var items = [{
      id: baseVariantId,
      quantity: baseQuantity,
      properties: input.baseProperties || {}
    }];

    if (dollarQuantity > 0) {
      items.push({
        id: dollarVariantId,
        quantity: dollarQuantity,
        properties: Object.assign({}, adjustmentProperties, {
          '_banner_adjustment_unit': 'dollar'
        })
      });
    }
    if (centQuantity > 0) {
      items.push({
        id: centVariantId,
        quantity: centQuantity,
        properties: Object.assign({}, adjustmentProperties, {
          '_banner_adjustment_unit': 'cent'
        })
      });
    }

    return {
      items: items,
      adjustmentItems: items.slice(1),
      adjustmentCents: adjustmentCents,
      dollarQuantity: dollarQuantity,
      centQuantity: centQuantity
    };
  }

  function variantRecipeMetrics(input) {
    var variantPriceCents = Math.round(toFiniteNumber(input.variantPriceCents, NaN));
    var addonCents = Math.round(toFiniteNumber(input.addonCents, 0));
    var orderQuantity = toFiniteNumber(input.orderQuantity, NaN);
    var tierQuantity = toFiniteNumber(input.tierQuantity, NaN);
    var turnaroundMultiplier = toFiniteNumber(input.turnaroundMultiplier, 1);
    var quantityMode = input.quantityMode === 'variant-tier' ? 'variant-tier' : 'line-item';

    if (!Number.isInteger(variantPriceCents) || variantPriceCents < 0 ||
        !Number.isInteger(addonCents) || addonCents < 0 ||
        !Number.isFinite(turnaroundMultiplier) || turnaroundMultiplier <= 0) {
      throw new Error('Variant recipe pricing configuration is incomplete.');
    }

    if (quantityMode === 'variant-tier') {
      if (!Number.isInteger(tierQuantity) || tierQuantity < 1) {
        throw new Error('A variant-tier quantity must be a whole number of at least one.');
      }
      orderQuantity = 1;
    } else if (!Number.isInteger(orderQuantity) || orderQuantity < 1) {
      throw new Error('Order quantity must be a whole number of at least one.');
    }

    var displayQuantity = quantityMode === 'variant-tier' ? tierQuantity : orderQuantity;
    var configuredUnitCents = variantPriceCents + addonCents;
    var totalCents = Math.round(configuredUnitCents * orderQuantity * turnaroundMultiplier);

    return {
      quantityMode: quantityMode,
      cartQuantity: orderQuantity,
      displayQuantity: displayQuantity,
      configuredUnitCents: configuredUnitCents,
      priceEachCents: totalCents / displayQuantity,
      totalCents: totalCents
    };
  }

  function optionRate(config, finishing, polePockets, windSlits, windSlitsSupported) {
    var adjustments = config.option_adjustments || {};
    var finishingRates = adjustments.finishing || {};
    var polePocketRates = adjustments.pole_pockets || {};
    var windSlitRates = adjustments.wind_slits || {};
    var finishingRate = toFiniteNumber(finishingRates[finishing], 0);
    var polePocketRate = polePockets === 'none'
      ? toFiniteNumber(polePocketRates.none, 0)
      : toFiniteNumber(polePocketRates.any_selected_edge, 0);
    var windSlitRate = windSlitsSupported
      ? toFiniteNumber(windSlitRates[windSlits], 0)
      : 0;

    return finishingRate + polePocketRate + windSlitRate;
  }

  function calculate(config, input, materialRecipe) {
    validateConfig(config);

    var widthInches = toFiniteNumber(input.widthInches, NaN);
    var heightInches = toFiniteNumber(input.heightInches, NaN);
    var quantity = toFiniteNumber(input.quantity, NaN);

    if (!Number.isFinite(widthInches) || widthInches <= 0 ||
        !Number.isFinite(heightInches) || heightInches <= 0) {
      throw new Error('Width and height must be greater than zero.');
    }

    if (!Number.isInteger(quantity) || quantity < 1) {
      throw new Error('Quantity must be a whole number of at least one.');
    }

    var squareFeetPerBanner = widthInches * heightInches / 144;
    var totalOrderSquareFeet = squareFeetPerBanner * quantity;
    var materialPricing = resolveMaterialPricing(config, materialRecipe);
    var baseRate = interpolateRate(config, totalOrderSquareFeet);
    var rate = baseRate * materialPricing.multiplier;
    var rawPricePerBanner = squareFeetPerBanner * rate;
    var minimum = materialPricing.minimumPricePerBanner;
    var basePricePerBanner = Math.max(rawPricePerBanner, minimum);
    var baseOrderPrice = basePricePerBanner * quantity;
    var adjustmentRate = optionRate(
      config,
      input.finishing,
      input.polePockets,
      input.windSlits,
      input.windSlitsSupported
    );
    var configuredBeforeTurnaround = baseOrderPrice * (1 + adjustmentRate);
    var turnaroundMultipliers = config.turnaround_multipliers || {};
    var turnaroundMultiplier = toFiniteNumber(turnaroundMultipliers[input.turnaround], 1);
    var totalPrice = configuredBeforeTurnaround * turnaroundMultiplier;

    return {
      widthInches: widthInches,
      heightInches: heightInches,
      quantity: quantity,
      squareFeetPerBanner: squareFeetPerBanner,
      totalOrderSquareFeet: totalOrderSquareFeet,
      baseInterpolatedRate: baseRate,
      materialRateMultiplier: materialPricing.multiplier,
      minimumPricePerBanner: minimum,
      interpolatedRate: rate,
      rawPricePerBanner: rawPricePerBanner,
      basePricePerBanner: basePricePerBanner,
      baseOrderPrice: baseOrderPrice,
      optionPercentage: adjustmentRate,
      configuredPriceBeforeTurnaround: configuredBeforeTurnaround,
      turnaroundMultiplier: turnaroundMultiplier,
      pricePerBanner: totalPrice / quantity,
      totalPrice: totalPrice,
      totalPriceCents: Math.round(totalPrice * 100)
    };
  }

  return {
    normalizeDimension: normalizeDimension,
    dimensionWithinLimits: dimensionWithinLimits,
    resolveMaterialTag: resolveMaterialTag,
    buildAdjustmentBundle: buildAdjustmentBundle,
    variantRecipeMetrics: variantRecipeMetrics,
    validateConfig: validateConfig,
    resolveMaterialPricing: resolveMaterialPricing,
    interpolateRate: interpolateRate,
    optionRate: optionRate,
    calculate: calculate
  };
});
